
import { useState, useEffect } from "react";
import { useMapStore } from "@/store/useMapStore";
import { CustomizerConfig } from "@/config/customizerConfig";

interface LabelsTabProps {
  config: CustomizerConfig;
}

const LabelsTab = ({ config }: LabelsTabProps) => {
  const { labels, location, setLabels } = useMapStore();
  const [lockCoordinates, setLockCoordinates] = useState(true);

  // Update coordinates in tagline when location changes and lock is enabled
  useEffect(() => {
    if (lockCoordinates && location && labels.showCoordinates) {
      const latDir = location.lat >= 0 ? 'N' : 'S';
      const lngDir = location.lng >= 0 ? 'E' : 'W';
      const coordinates = `${Math.abs(location.lat).toFixed(3)}\u00B0${latDir} / ${Math.abs(location.lng).toFixed(3)}\u00B0${lngDir}`;
      setLabels({ tagline: coordinates });
    }
  }, [location, lockCoordinates, labels.showCoordinates, setLabels]);

  const handleLabelChange = (field: 'headline' | 'divider' | 'tagline', value: string) => {
    setLabels({ [field]: value });
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: "var(--font-body)",
    borderColor: "var(--brand-border)",
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = "var(--sage-400)";
    e.currentTarget.style.boxShadow = "0 0 0 2px var(--sage-100)";
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = "var(--brand-border)";
    e.currentTarget.style.boxShadow = "none";
  };

  return (
    <div className="p-6 space-y-6">
      <h3
        className="text-lg mb-4"
        style={{ fontFamily: "var(--font-heading)", color: "var(--brand-text)" }}
      >
        Customize Labels
      </h3>

      <div className="space-y-4">
        {/* Title (maps to headline) */}
        <div>
          <label
            className="text-sm block mb-1.5"
            style={{ color: "var(--brand-muted)", fontFamily: "var(--font-body)" }}
          >
            Title
          </label>
          <input
            type="text"
            value={labels.headline}
            onChange={(e) => handleLabelChange('headline', e.target.value)}
            placeholder="MANHATTAN"
            className="w-full px-4 py-3 rounded-xl border bg-white text-sm font-medium tracking-wide outline-none transition-all"
            style={inputStyle}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>

        {/* Subtitle (maps to divider) */}
        <div>
          <label
            className="text-sm block mb-1.5"
            style={{ color: "var(--brand-muted)", fontFamily: "var(--font-body)" }}
          >
            Subtitle
          </label>
          <input
            type="text"
            value={labels.divider}
            onChange={(e) => handleLabelChange('divider', e.target.value)}
            placeholder="40.7831° N / 73.9712° W"
            className="w-full px-4 py-3 rounded-xl border bg-white text-sm outline-none transition-all"
            style={inputStyle}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>

        {/* Tagline */}
        <div>
          <label
            className="text-sm block mb-1.5"
            style={{ color: "var(--brand-muted)", fontFamily: "var(--font-body)" }}
          >
            Tagline
          </label>
          <input
            type="text"
            value={labels.tagline}
            onChange={(e) => handleLabelChange('tagline', e.target.value)}
            disabled={lockCoordinates}
            placeholder="The city that never sleeps"
            className="w-full px-4 py-3 rounded-xl border bg-white text-sm italic outline-none transition-all disabled:opacity-60"
            style={inputStyle}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />

          {/* Lock coordinates toggle */}
          <label className="flex items-center gap-2 mt-3 cursor-pointer">
            <input
              type="checkbox"
              checked={lockCoordinates}
              onChange={(e) => {
                setLockCoordinates(e.target.checked);
                setLabels({ showCoordinates: e.target.checked });
              }}
              className="w-4 h-4 rounded"
              style={{ accentColor: "var(--sage-400)" }}
            />
            <span
              className="text-sm"
              style={{ color: "var(--brand-muted)", fontFamily: "var(--font-body)" }}
            >
              Lock to current coordinates
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default LabelsTab;
